import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { MonitorSmartphone, Loader2, RefreshCw, Repeat, Zap, BookOpen } from 'lucide-react';
import { NowShowingCard } from '../components/vestaboard/NowShowingCard';
import { GlobalSettingsCard } from '../components/vestaboard/GlobalSettingsCard';
import { RotationList } from '../components/vestaboard/RotationList';
import { BoardCard } from '../components/vestaboard/BoardCard';
import { CustomMessageCard } from '../components/vestaboard/CustomMessageCard';
import { HistoryCard } from '../components/vestaboard/HistoryCard';

export default function VestaboardManagerPage() {
  const [status, setStatus] = useState(null);
  const [boards, setBoards] = useState(null);
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(() => api.vbManagerStatus().then(r => setStatus(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Status konnte nicht geladen werden')), []);
  const loadBoards = useCallback(() => api.vbManagerBoards().then(r => setBoards(r.data.boards || [])).catch(() => {}), []);
  const reload = useCallback(async () => { await Promise.all([loadStatus(), loadBoards()]); setTick(t => t + 1); }, [loadStatus, loadBoards]);

  useEffect(() => { reload().then(() => setLoading(false)); }, [reload]);
  useEffect(() => {
    const id = setInterval(loadStatus, 10000);
    const id2 = setInterval(loadBoards, 60000);
    return () => { clearInterval(id); clearInterval(id2); };
  }, [loadStatus, loadBoards]);

  const rotation = (boards || []).filter(b => b.kind === 'rotation').sort((a, b) => (a.settings.order ?? 100) - (b.settings.order ?? 100));
  const events = (boards || []).filter(b => b.kind === 'event' && b.id !== 'custom_message').sort((a, b) => (a.settings.priority ?? 9) - (b.settings.priority ?? 9));
  const custom = (boards || []).find(b => b.id === 'custom_message');

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="vestaboard-manager-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <MonitorSmartphone className="w-7 h-7 text-amber-400" /> Vestaboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Steuert alles, was die Note im Büro zeigt — Boards, Zeitplan, Events. Nur Projektnamen, nie Firmennamen.
            {status && !status.configured && <span className="text-amber-300"> · Kein VESTABOARD_TOKEN hinterlegt — es kann nichts gesendet werden.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="https://docs.vestaboard.com/docs/characterCodes" target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> Zeichencodes</a>
          <button onClick={reload} className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-border text-muted-foreground hover:text-foreground" title="Neu laden" data-testid="vb-reload-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {loading && !status ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Lade Board Manager…</div>
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><NowShowingCard status={status} onChanged={reload} /></div>
            <GlobalSettingsCard settings={status?.settings} onChanged={reload} />
          </div>

          <section>
            <h2 className="text-base md:text-lg font-heading text-foreground flex items-center gap-2 mb-1"><Repeat className="w-4 h-4 text-sky-300" /> Rotation-Boards <span className="text-xs text-muted-foreground font-normal">· {rotation.length} · Reihenfolge per Drag & Drop</span></h2>
            <p className="text-[11px] text-muted-foreground mb-3">Zeigen reihum, jedes für seine Anzeigezeit. Tage/Zeitfenster und „exklusiv“ steuern, wann ein Board dran ist.</p>
            <div className="pl-6">{boards && <RotationList boards={rotation} onChanged={reload} />}</div>
          </section>

          <section>
            <h2 className="text-base md:text-lg font-heading text-foreground flex items-center gap-2 mb-1"><Zap className="w-4 h-4 text-orange-300" /> Event-Boards <span className="text-xs text-muted-foreground font-normal">· {events.length}</span></h2>
            <p className="text-[11px] text-muted-foreground mb-3">Unterbrechen die Rotation nach Priorität (1 = höchste), dann läuft der Zeitplan weiter. „Simulieren“ schickt ein Test-Event (PROJECT SAMPLE) durch den ganzen Ablauf.</p>
            {events.length === 0 ? (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-sm px-4 py-6 text-center" data-testid="vb-no-event-boards">Noch keine Event-Boards — kommen in Phase 3 (Stage-Wechsel, QR-Scans, Response-Alert).</p>
            ) : (
              <div className="grid lg:grid-cols-2 gap-3">{events.map(b => <BoardCard key={b.id} board={b} onChanged={reload} />)}</div>
            )}
          </section>

          <CustomMessageCard defaultSeconds={custom?.settings?.extra?.default_display_seconds || custom?.settings?.display_seconds || 600} onChanged={reload} />
          <HistoryCard refreshKey={tick} />
        </>
      )}
    </div>
  );
}
